import type { HandwrittenNoteTypes } from '@mathflat/handwritten-note'
import { subMonths } from 'date-fns'
import type { DBSchema, IDBPDatabase, IDBPObjectStore, StoreNames } from 'idb'
import { openDB } from 'idb'

type NoteData = HandwrittenNoteTypes.NoteData

const NOTE_VALID_MONTHS = 6

// ★ IndexedDB schema — 본 service / integration write (handleStudentMainLogic) /
//   인라인은 import 불가 → window.__INTEGRATION_CONSTANTS__.INDEXED_DB 로 빌드 시점 주입.
export const HANDWRITTEN_NOTE_DB_NAME = 'handwritten-note'
export const STUDENT_WORKSHEET_NOTE_STORE = 'studentWorksheetNote'
export const STUDENT_WORKSHEET_NOTE_KEY_PATH: readonly string[] = [
  'studentWorksheetId',
  'worksheetProblemId',
]

interface StudentWorksheetNoteKey {
  studentWorksheetId: number
  worksheetProblemId: number
}

export interface StudentWorksheetNoteValue extends StudentWorksheetNoteKey {
  noteData: NoteData
  timestamp: number
}

interface ObjectStore extends DBSchema {
  studentWorksheetNote: {
    key: number[]
    value: StudentWorksheetNoteValue
  }
}

const getNoteKeyFromValue = (value: StudentWorksheetNoteValue) => {
  return {
    studentWorksheetId: value.studentWorksheetId,
    worksheetProblemId: value.worksheetProblemId,
  }
}

export class IndexedDbService {
  private db?: IDBPDatabase<ObjectStore>

  private _isConnectionValid(): boolean {
    if (!this.db) return false

    try {
      // Check if the database connection is still valid
      // If the connection is closed, accessing objectStoreNames will throw
      return this.db.objectStoreNames.length >= 0
    } catch {
      return false
    }
  }

  private async _ensureConnection() {
    if (!this.db || !this._isConnectionValid()) {
      await this.open()
    }
    if (!this.db) {
      throw new Error('db not initialized')
    }
  }

  private async _transaction(mode: 'readonly'): Promise<{
    store: IDBPObjectStore<
      ObjectStore,
      StoreNames<ObjectStore>[],
      'studentWorksheetNote',
      'readonly'
    >
    done: () => Promise<void>
  }>
  private async _transaction(mode: 'readwrite'): Promise<{
    store: IDBPObjectStore<
      ObjectStore,
      StoreNames<ObjectStore>[],
      'studentWorksheetNote',
      'readwrite'
    >
    done: () => Promise<void>
  }>
  private async _transaction(mode: 'readonly' | 'readwrite'): Promise<{
    store:
      | IDBPObjectStore<ObjectStore, StoreNames<ObjectStore>[], 'studentWorksheetNote', 'readonly'>
      | IDBPObjectStore<ObjectStore, StoreNames<ObjectStore>[], 'studentWorksheetNote', 'readwrite'>
    done: () => Promise<void>
  }> {
    await this._ensureConnection()
    const tx = this.db!.transaction(STUDENT_WORKSHEET_NOTE_STORE, mode)
    return {
      store: tx.store as
        | IDBPObjectStore<
            ObjectStore,
            StoreNames<ObjectStore>[],
            'studentWorksheetNote',
            'readonly'
          >
        | IDBPObjectStore<
            ObjectStore,
            StoreNames<ObjectStore>[],
            'studentWorksheetNote',
            'readwrite'
          >,
      done: async () => await tx.done,
    }
  }

  async open() {
    const isValid = this._isConnectionValid()
    if (this.db && isValid) {
      return
    }
    // Clear invalid connection
    if (this.db && !isValid) {
      this.db = undefined
    }
    const database = await openDB<ObjectStore>(HANDWRITTEN_NOTE_DB_NAME, 1, {
      upgrade(db) {
        db.createObjectStore(STUDENT_WORKSHEET_NOTE_STORE, {
          keyPath: [...STUDENT_WORKSHEET_NOTE_KEY_PATH],
        })
      },
      /* 
        Called if there are older versions of the database open on the origin, so this version cannot open. 
        This is similar to the blocked event in plain IndexedDB.
       */
      blocked: () => {
        this.db?.close()
        window.location.reload()
      },
      /*
        Called if this connection is blocking a future version of the database from opening.
        This is similar to the versionchange event in plain IndexedDB.
       */
      blocking: () => {
        this.db?.close()
        window.location.reload()
      },
    })
    this.db = database
  }

  private _getStudentWorksheetCompositKey(key: StudentWorksheetNoteKey) {
    return [key.studentWorksheetId, key.worksheetProblemId]
  }

  async getStudentWorksheetNote(key: StudentWorksheetNoteKey) {
    const { store, done } = await this._transaction('readonly')
    const data = await store.get(this._getStudentWorksheetCompositKey(key))
    await done()
    return data
  }

  async getStudentWorksheetNotes(keys: StudentWorksheetNoteKey[]) {
    const { store, done } = await this._transaction('readonly')
    const tasks = keys.map((key) => {
      return store.get(this._getStudentWorksheetCompositKey(key))
    })
    const data = await Promise.all(tasks)
    await done()
    return data.filter((item) => Boolean(item)) as StudentWorksheetNoteValue[]
  }

  async getAllStudentWorksheetNotes() {
    const { store, done } = await this._transaction('readonly')
    const data = await store.getAll()
    await done()
    return data
  }

  async putStudentWorksheetNote(value: StudentWorksheetNoteValue) {
    try {
      const { store, done } = await this._transaction('readwrite')
      await store.put(value)
      await done()
    } catch (err) {
      if (err instanceof Error && err.name === 'QuotaExceededError') {
        const expiredTimestamp = subMonths(new Date(), NOTE_VALID_MONTHS).getTime()
        const allNotes = await this.getAllStudentWorksheetNotes()

        const nonCurrentWorksheetNotes = allNotes.filter(
          (note) => note.studentWorksheetId !== value.studentWorksheetId,
        )

        let targetNotes = nonCurrentWorksheetNotes.filter(
          (note) => !note.timestamp || note.timestamp <= expiredTimestamp,
        )

        if (!targetNotes.length) {
          // 삭제할 노트 없으면 현재 풀이중인 학습지 필기 외에 전부 삭제
          targetNotes = [...nonCurrentWorksheetNotes]
        }

        if (targetNotes.length) {
          await this.deleteStudentWorksheetNotes(targetNotes.map(getNoteKeyFromValue))
          // Retry after cleanup
          const { store: retryStore, done: retryDone } = await this._transaction('readwrite')
          await retryStore.put(value)
          await retryDone()
          return
        }
      }
      throw err
    }
  }

  async deleteStudentWorksheetNotes(keys: StudentWorksheetNoteKey[]) {
    const { store, done } = await this._transaction('readwrite')
    const tasks = keys.map((key) => {
      return store.delete(this._getStudentWorksheetCompositKey(key))
    })
    await Promise.all(tasks)
    await done()
  }

  close() {
    if (this.db) {
      this.db.close()
      this.db = undefined
    }
  }
}
